
import {
  ensurePermissionsAndCheck,
  evaluatePermission,
  watchAbilities,
  type PermissionCheckInput,
} from "@/core/helpers/checkAbility";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";

export default defineComponent({
  name: "Can",
  props: {
    /**
     * All permissions required (all-of logic)
     */
    all: {
      type: Array as () => string[],
      default: undefined,
    },
    /**
     * Any permission required (any-of logic)
     */
    any: {
      type: Array as () => string[],
      default: undefined,
    },
    /**
     * Invert the permission check logic
     */
    not: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isReady = ref(false);
    let stopWatch: (() => void) | null = null;

    const permissionInput = computed((): PermissionCheckInput => {
      if (props.all && props.all.length > 0) {
        return { value: props.all, not: props.not };
      }

      if (props.any && props.any.length > 0) {
        return { value: props.any, any: true, not: props.not };
      }

      return { value: undefined, not: props.not };
    });

    const hasPermission = computed(() => {
      if (!isReady.value) {
        return false;
      }

      return evaluatePermission(permissionInput.value);
    });

    const refreshPermissions = async () => {
      await ensurePermissionsAndCheck(permissionInput.value);
      isReady.value = true;
    };

    onMounted(async () => {
      await refreshPermissions();
      stopWatch = watchAbilities(() => {
        void refreshPermissions();
      });
    });

    onBeforeUnmount(() => {
      stopWatch?.();
      stopWatch = null;
    });

    return {
      isReady,
      hasPermission,
    };
  },
});
